import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  Layout,
  FormFieldInputNew,
  ThreeDotLoader,
  Notification,
} from "@netapp/bxp-design-system-react";
import { useTranslation } from "react-i18next";
import "./Subscribe.scss";
import UnsubscribeOtpModal from "./UnsubscribeOtpModal";

const UnsubscribeModal = ({
  advisoryId,
  headerText,
  notification,
  setNotification,
}) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  const [captchaToken, setCaptchaToken] = useState("");
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const recaptchaWidgetIdRef = useRef(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // Mobile detection for responsive reCAPTCHA and layout
  const [isMobile, setIsMobile] = useState(false);

  // Focus the email field when modal opens (better mobile UX)
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Hash router: open when URL ends with #unsubscribe
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#unsubscribe") setOpen(true);
      else setOpen(false);
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  // Load Google reCAPTCHA script once
  useEffect(() => {
    const scriptId = "recaptcha-script";
    const handleLoad = () => setRecaptchaLoaded(true);
    const handleError = () => console.error("Failed to load reCAPTCHA script");
    let script = document.getElementById(scriptId);

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    if (window.grecaptcha) handleLoad();
    else {
      script.addEventListener("load", handleLoad);
      script.addEventListener("error", handleError);
    }

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, []);

  // Render reCAPTCHA explicitly and track widget id to avoid duplicate renders.
  useEffect(() => {
    if (!recaptchaLoaded || !open || !window.grecaptcha) return;
    if (!recaptchaSiteKey) {
      console.error("Missing REACT_APP_RECAPTCHA_SITE_KEY env variable");
      return;
    }

    window.grecaptcha.ready(() => {
      try {
        const container = document.getElementById("unsub-recaptcha-container");
        if (!container) return;

        if (recaptchaWidgetIdRef.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetIdRef.current);
          return;
        }

        recaptchaWidgetIdRef.current = window.grecaptcha.render(container, {
          sitekey: recaptchaSiteKey,
          callback: (token) => {
            setCaptchaToken(token);
            setIsCaptchaVerified(true);
            // remove any previous inline error
            const errorDiv = document.querySelector(".captcha-div1");
            if (errorDiv) errorDiv.innerHTML = "";
          },
          theme: "light",
          size: isMobile ? "compact" : "normal",
        });
      } catch (error) {
        console.error("Error rendering reCAPTCHA:", error);
      }
    });
  }, [recaptchaLoaded, open, isMobile, recaptchaSiteKey]);

  // Reset captcha state when modal closes
  useEffect(() => {
    if (!open) {
      if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
        try {
          window.grecaptcha.reset(recaptchaWidgetIdRef.current);
        } catch (_) {
          // noop
        }
      }
      recaptchaWidgetIdRef.current = null;
      setCaptchaToken("");
      setIsCaptchaVerified(false);
    }
  }, [open]);

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val));

  const showCaptchaError = () => {
    const captchaDiv = document.querySelector(".captcha-div1");
    if (captchaDiv) {
      captchaDiv.innerHTML =
        `<span class="luci-form__field-error" role="alert">${t("modal_verifyRecaptcha.other")}</span>`;
    }
    setIsCaptchaVerified(false);
  };

  const handleUnsubscribe = async () => {
    const trimmed = String(email || "").trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      alert(t("modal_enterValidEmail.other"));
      return;
    }
    // Client-side gate only: ensure the user actually solved the widget.
    // The real verification happens server-side inside /unsub_adv so the
    // single-use token isn't burned by a pre-flight call.
    if (!captchaToken) {
      showCaptchaError();
      return;
    }

    setIsLoading(true);

    const url = `${process.env.REACT_APP_API_BASE_URL}/unsub_adv`;
    // Backend expects `captcha_token`; the server verifies it against
    // Google's siteverify API before initiating the unsubscribe OTP.
    const body = { adv_id: advisoryId, email: trimmed, captcha_token: captchaToken };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = new Error("Network response was not ok");
        err.status = response.status;
        throw err;
      }

      const data = await response.json();
      console.log("Unsubscription initiated:", data);

      setNotification({
        visible: true,
        message: t("modal_checkEmailForOtp.other"),
        type: "success",
      });

      setOpen(false);
      setOtpModalOpen(true);
    } catch (error) {
      console.error("Unsubscription failed:", error);
      // 400 from /unsub_adv means the backend rejected the captcha; show
      // the inline captcha error rather than a generic failure toast.
      if (error && error.status === 400) {
        showCaptchaError();
        // Reset the widget so the user can solve a fresh challenge.
        if (window.grecaptcha) {
          try { window.grecaptcha.reset(); } catch (_) { /* noop */ }
        }
        setCaptchaToken("");
        setNotification({
          visible: true,
          message: t("modal_verifyRecaptcha.other"),
          type: "error",
        });
      } else {
        setNotification({
          visible: true,
          message: t("modal_unsubscriptionFailed.other"),
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setNotification({ visible: false, message: "", type: "success" });
      }, 4000);
    }
  };

  // Submit on Enter key (only once the captcha has been solved)
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !isLoading && email && isCaptchaVerified) {
      setIsLoading(true);
      handleUnsubscribe();
    }
  };

  return (
    <>
      {open && (
        <Modal className="subscribe-modal">
          <ModalHeader className="modal-header">
            {headerText || t("modal_unsubscribeFromUpdates.other", { id: advisoryId })}
          </ModalHeader>

          {/* Make content scrollable on small screens */}
          <ModalContent className="modal-content">
            {notification?.visible && (
              <Notification type={notification.type}>
                {notification.message}
              </Notification>
            )}

            <Layout.Grid>
              <Layout.GridItem lg={12}>
                <FormFieldInputNew
                  ref={inputRef}
                  className="form-field-input-new"
                  label={t("modal_email.other")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={t("modal_emailPlaceholder.other")}
                />
              </Layout.GridItem>

              <Layout.GridItem lg={12}>
                <div id="unsub-recaptcha-container" className="g-recaptcha" />
                <div className="captcha-div1" />
              </Layout.GridItem>
            </Layout.Grid>
          </ModalContent>

          {/* Stack buttons full-width on mobile using the same footer class */}
          <ModalFooter className="subscribe-modal-footer">
            <Button color="secondary" onClick={() => setOpen(false)} disabled={isLoading}>
              {t("modal_cancel.other")}
            </Button>
            <Button
              onClick={() => {
                setIsLoading(true);
                handleUnsubscribe();
              }}
              disabled={!email || !isCaptchaVerified || isLoading}
            >
              {isLoading ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ThreeDotLoader />
                  <span style={{ marginLeft: 8 }}>{t("modal_unsubscribe.other")}</span>
                </div>
              ) : (
                t("modal_unsubscribe.other")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* OTP modal for unsubscribe confirmation */}
      <UnsubscribeOtpModal
        email={email}
        advisoryId={advisoryId}
        open={otpModalOpen}
        setOpen={setOtpModalOpen}
        notification={notification}
        setNotification={setNotification}
      />
    </>
  );
};

export default UnsubscribeModal;